import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import {
  signInWithRedirect,
  GoogleAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import { Capacitor } from "@capacitor/core";
import { getFirebaseAuth } from "../settings/firebaseConfig";

const isNativePlatform = Capacitor.isNativePlatform();

// On native we let the NATIVE Firebase SDK complete the sign-in (skipNativeAuth
// is false in capacitor.config), then read the Firebase ID token from the plugin.
// This deliberately avoids the JS SDK's signInWithCredential, whose network call
// runs through the WKWebView fetch stack and fails with
// auth/network-request-failed on a cold app start (the "restart the app once or
// twice to sign in" bug). The native SDK mints the token over URLSession, which
// is not affected by that cold-start hiccup.
const getNativeIdTokenRetry = async (attempts = 5) => {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await FirebaseAuthentication.getIdToken();
      if (res?.token) return res.token;
      lastErr = new Error("Firebase ID token non disponibile.");
    } catch (err) {
      lastErr = err;
    }
    // 300/600/900/1200ms backoff — covers the brief window where the native
    // user exists but the token endpoint isn't reachable yet.
    await new Promise((r) => setTimeout(r, 300 * (i + 1)));
  }
  throw lastErr;
};

// Mirror the firebase/auth UserCredential shape ({ user }) the Login page
// expects: it does `if (result?.user) authCheck(result.user)`, and authCheck
// calls user.getIdToken(). Returning a bare token object (no `.user`) makes
// Login skip authCheck entirely and the sign-in silently stalls.
const nativeUserFromToken = (token) => ({
  user: { getIdToken: async () => token },
});

// Decode the `email` claim out of an Apple identity token (JWT). Apple only
// surfaces the real address in this token when the user picks "Share My Email";
// Firebase persists the relay from the first "Hide" auth and never updates its
// stored user.email, so the Apple idToken is the ONLY place the shared real
// email is available to us.
const normalizeEmail = (value) => {
  const email = String(value || "")
    .trim()
    .toLowerCase();
  return email || null;
};

const emailFromAppleIdToken = (appleIdToken) => {
  try {
    const part = String(appleIdToken || "").split(".")[1];
    if (!part) return null;
    let b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "="; // base64url tokens often drop padding
    const json = JSON.parse(window.atob(b64));
    return normalizeEmail(json?.email);
  } catch (_) {
    return null;
  }
};

// Decode the `name` claim out of a JWT. Apple only returns the full name on the
// FIRST authorization (via result.user.displayName); on later / recovery
// sign-ins that's null, but the native Firebase SDK persists the name into the
// Firebase ID token's `name` claim, so it stays recoverable there.
const nameFromIdToken = (idToken) => {
  try {
    const part = String(idToken || "").split(".")[1];
    if (!part) return null;
    let b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const json = JSON.parse(window.atob(b64));
    const n = String(json?.name || "").trim();
    return n || null;
  } catch (_) {
    return null;
  }
};

// One-shot carrier for the email Apple shared on the most recent sign-in, so the
// caller (Login authCheck) can forward it to /api/social-login. Attaching it to
// the Firebase UserCredential isn't reliable (the object may be frozen).
let lastAppleProviderEmail = null;
export const consumeAppleProviderEmail = () => {
  const e = lastAppleProviderEmail;
  lastAppleProviderEmail = null;
  return e;
};

// The raw Apple identity token (JWT) from the most recent Apple sign-in, so the
// backend can decode/verify it and extract the email claim authoritatively —
// Firebase's stored user.email is unreliable (see emailFromAppleIdToken).
let lastAppleRawIdToken = null;
export const consumeAppleRawIdToken = () => {
  const t = lastAppleRawIdToken;
  lastAppleRawIdToken = null;
  return t;
};

// Split Apple's full name ("Given Family") into { name, surname }.
const splitAppleFullName = (full) => {
  const parts = String(full || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return null;
  if (parts.length === 1) return { name: parts[0], surname: "" };
  return { name: parts[0], surname: parts.slice(1).join(" ") };
};

// The name Apple returns — ONLY on the very first authorization — captured so
// onboarding can prefill Nome/Cognome and never force the user to re-type a name
// Apple already provides (App Store "Sign in with Apple" requirement). Kept in a
// one-shot module carrier + localStorage so it survives a reload before onboarding.
let lastAppleProviderName = null;
export const consumeAppleProviderName = () => {
  let n = lastAppleProviderName;
  lastAppleProviderName = null;
  if (!n) {
    try {
      const raw = localStorage.getItem("apple_provider_name");
      if (raw) n = JSON.parse(raw);
    } catch (_) {}
  }
  // One-shot: clear so a later non-Apple sign-in doesn't inherit a stale name
  // (and doesn't wrongly lock the onboarding fields).
  try {
    localStorage.removeItem("apple_provider_name");
  } catch (_) {}
  return n && n.name ? n : null;
};

export const signInWithGoogle = async () => {
  try {
    if (isNativePlatform) {
      // Native SDK completes the Firebase sign-in; read the ID token natively.
      await FirebaseAuthentication.signInWithGoogle();
      const token = await getNativeIdTokenRetry();
      return nativeUserFromToken(token);
    }
    const provider = new GoogleAuthProvider();
    return await signInWithRedirect(getFirebaseAuth(), provider);
  } catch (error) {
    console.error("Error in signInWithGoogle:", error);
    throw error;
  }
};

export const signInWithApple = async () => {
  try {
    if (isNativePlatform) {
      const result = await FirebaseAuthentication.signInWithApple({
        scopes: ["email", "name"],
      });
      // Capture the email Apple shared (real when "Condividi la mia email" was
      // picked). Apple only puts the email in the identity token / credential on
      // a fresh consent, so probe every source the plugin can surface it in.
      const appleIdToken = result?.credential?.idToken;
      lastAppleRawIdToken = appleIdToken || null;
      const emailFromToken = appleIdToken
        ? emailFromAppleIdToken(appleIdToken)
        : null;
      const emailFromUser = normalizeEmail(result?.user?.email);
      const emailFromProfile = normalizeEmail(
        result?.additionalUserInfo?.profile?.email,
      );
      lastAppleProviderEmail =
        emailFromToken || emailFromUser || emailFromProfile || null;
      // Diagnostic: shows in device logs which source (if any) carried the email.
      console.log(
        "[apple] emailCapture",
        JSON.stringify({
          hasIdToken: !!appleIdToken,
          emailFromToken,
          emailFromUser,
          emailFromProfile,
          chosen: lastAppleProviderEmail,
        }),
      );
      // Native SDK completes the Firebase sign-in; read the ID token natively.
      const token = await getNativeIdTokenRetry();
      // Capture Apple's full name so onboarding can prefill Nome/Cognome without
      // asking the user to re-type it (App Store "Sign in with Apple" rule).
      // Apple returns the name only on the FIRST consent (result.user.displayName);
      // on later / recovery sign-ins that's null, so fall back to the `name` claim
      // the native Firebase SDK keeps in the Firebase ID token.
      const appleName =
        splitAppleFullName(result?.user?.displayName) ||
        splitAppleFullName(result?.additionalUserInfo?.profile?.name) ||
        splitAppleFullName(nameFromIdToken(token));
      lastAppleProviderName = appleName;
      if (appleName) {
        try {
          localStorage.setItem("apple_provider_name", JSON.stringify(appleName));
        } catch (_) {}
      }
      // Diagnostic: shows in device logs which source carried the name.
      console.log(
        "[apple] nameCapture",
        JSON.stringify({
          displayName: result?.user?.displayName || null,
          fromToken: nameFromIdToken(token),
          chosen: appleName,
        }),
      );
      return nativeUserFromToken(token);
    }
    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    provider.addScope("name");
    return await signInWithRedirect(getFirebaseAuth(), provider);
  } catch (error) {
    console.error("Error in signInWithApple:", error);
    console.error("Error code:", error?.code);
    console.error("Error message:", error?.message);
    throw error;
  }
};

export const signInWithMicrosoft = async () => {
  try {
    if (isNativePlatform) {
      // Native SDK completes the Firebase sign-in; read the ID token natively.
      await FirebaseAuthentication.signInWithMicrosoft();
      const token = await getNativeIdTokenRetry();
      return nativeUserFromToken(token);
    }
    const provider = new OAuthProvider("microsoft.com");
    provider.setCustomParameters({ prompt: "select_account" });
    return await signInWithRedirect(getFirebaseAuth(), provider);
  } catch (error) {
    console.error("Error in signInWithMicrosoft:", error);
    throw error;
  }
};

export const getCurrentUser = async () => {
  if (isNativePlatform) {
    return await FirebaseAuthentication.getCurrentUser();
  }
  return getFirebaseAuth().currentUser;
};

export const signOut = async () => {
  if (isNativePlatform) {
    await FirebaseAuthentication.signOut();
  }
  return getFirebaseAuth().signOut();
};

export { getFirebaseAuth as auth };
